import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";

// CSS styles in JavaScript
const styles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "linear-gradient(90deg, #0D47A1 0%, #1976D2 100%)",
    color: "#ffffff",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    fontFamily: "'Roboto', sans-serif",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "12px 20px",
  },
  navbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
  },
  logo: {
    width: "32px",
    height: "32px",
    marginRight: "12px",
    color: "#BBDEFB",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: "700",
    letterSpacing: "-0.5px",
    margin: 0,
  },
  desktopNav: {
    display: "flex",
    alignItems: "center",
  },
  link: {
    textDecoration: "none",
    color: "#ffffff",
    padding: "8px 16px",
    margin: "0 4px",
    borderRadius: "4px",
    fontWeight: "500",
    transition: "all 0.2s ease",
  },
  activeLink: {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
  },
  linkHover: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    transform: "translateY(-2px)",
  },
  ctaButton: {
    backgroundColor: "#E3F2FD",
    color: "#0D47A1",
    border: "none",
    padding: "8px 20px",
    marginLeft: "12px",
    borderRadius: "4px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textDecoration: "none",
  },
  ctaButtonHover: {
    backgroundColor: "#ffffff",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  menuButton: {
    display: "none",
    background: "none",
    border: "none",
    color: "#ffffff",
    fontSize: "24px",
    cursor: "pointer",
    padding: "4px",
  },
  mobileNav: {
    display: "none",
    flexDirection: "column",
    padding: "12px 0",
  },
  mobileLink: {
    textDecoration: "none",
    color: "#ffffff",
    padding: "12px 16px",
    borderRadius: "4px",
    fontWeight: "500",
    margin: "4px 0",
    transition: "background-color 0.2s ease",
  },
  mobileCta: {
    backgroundColor: "#E3F2FD",
    color: "#0D47A1",
    border: "none",
    padding: "12px 20px",
    marginTop: "12px",
    borderRadius: "4px",
    fontWeight: "600",
    cursor: "pointer",
    width: "100%",
    textAlign: "center",
    textDecoration: "none",
    display: "block",
  },
  // Media queries will be handled with JavaScript
};

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(false);
  const location = useLocation();

  // Check if a route is active
  const isActive = (path) => location.pathname === path;
  
  // Update styles for mobile view
  const isMobile = window.innerWidth < 768;
  if (isMobile) {
    styles.desktopNav.display = "none";
    styles.menuButton.display = "block";
    if (isMenuOpen) {
      styles.mobileNav.display = "flex";
    }
  }

  // Check if user is authenticated (simple check - improve based on your auth system)
  const isAuthenticated = sessionStorage.getItem('token') || localStorage.getItem('token');

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <div style={styles.navbar}>
          {/* Title */}
          <div style={styles.logoContainer}>
            <h1 style={styles.title}>SportsHive</h1>
          </div>
          
          {/* Desktop navigation */}
          <nav style={styles.desktopNav}>
            <Link 
              to="/" 
              style={{
                ...styles.link,
                ...(isActive("/") ? styles.activeLink : {}),
                ...(hoveredLink === "home" ? styles.linkHover : {})
              }}
              onMouseEnter={() => setHoveredLink("home")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              Home
            </Link>
            <Link 
              to="/features" 
              style={{
                ...styles.link,
                ...(isActive("/features") ? styles.activeLink : {}),
                ...(hoveredLink === "features" ? styles.linkHover : {})
              }}
              onMouseEnter={() => setHoveredLink("features")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              Features
            </Link>
            <Link 
              to="/my-teams" 
              style={{
                ...styles.link,
                ...(isActive("/my-teams") ? styles.activeLink : {}),
                ...(hoveredLink === "dashboard" ? styles.linkHover : {})
              }}
              onMouseEnter={() => setHoveredLink("dashboard")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              My Teams
            </Link>
            
            {/* Support link - shown to everyone */}
            <Link
              to="/support"
              style={{
                ...styles.link,
                ...(isActive("/support") ? styles.activeLink : {}),
                ...(hoveredLink === "support" ? styles.linkHover : {})
              }}
              onMouseEnter={() => setHoveredLink("support")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              Support
            </Link>
            
            {/* My Account link - now shown to everyone */}
            <Link
              to="/my-account"
              style={{
                ...styles.link,
                ...(isActive("/my-account") ? styles.activeLink : {}),
                ...(hoveredLink === "myAccount" ? styles.linkHover : {})
              }}
              onMouseEnter={() => setHoveredLink("myAccount")}
              onMouseLeave={() => setHoveredLink(null)}
            >
              My Account
            </Link>
            
            {/* Conditional rendering for team options */}
            {isAuthenticated ? (
              <Link
                to="/team-options"
                style={{
                  ...styles.link,
                  ...(isActive("/team-options") ? styles.activeLink : {}),
                  ...(hoveredLink === "teamOptions" ? styles.linkHover : {})
                }}
                onMouseEnter={() => setHoveredLink("teamOptions")}
                onMouseLeave={() => setHoveredLink(null)}
              >
                Team Options
              </Link>
            ) : (
              <Link 
                to="/login" 
                style={{
                  ...styles.ctaButton,
                  ...(hoveredButton ? styles.ctaButtonHover : {})
                }}
                onMouseEnter={() => setHoveredButton(true)}
                onMouseLeave={() => setHoveredButton(false)}
              >
                Log In
              </Link>
            )}
            
            {/* Sign Up CTA - only show if not authenticated */}
            {!isAuthenticated && (
              <Link 
                to="/signup" 
                style={{
                  ...styles.ctaButton,
                  ...(hoveredButton ? styles.ctaButtonHover : {})
                }}
                onMouseEnter={() => setHoveredButton(true)}
                onMouseLeave={() => setHoveredButton(false)}
              >
                Sign Up
              </Link>
            )}
          </nav>
          
          {/* Mobile menu button */}
          <button 
            style={styles.menuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? "✕" : "☰"}
          </button>
        </div>
        
        {/* Mobile navigation */}
        <nav style={{
          ...styles.mobileNav,
          display: isMenuOpen && isMobile ? "flex" : "none"
        }}>
          <Link 
            to="/" 
            style={{
              ...styles.mobileLink,
              ...(isActive("/") ? styles.activeLink : {})
            }}
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>
          <Link 
            to="/features" 
            style={{
              ...styles.mobileLink,
              ...(isActive("/features") ? styles.activeLink : {})
            }}
            onClick={() => setIsMenuOpen(false)}
          >
            Features
          </Link>
          <Link 
            to="/my-teams" 
            style={{
              ...styles.mobileLink,
              ...(isActive("/my-teams") ? styles.activeLink : {})
            }}
            onClick={() => setIsMenuOpen(false)}
          >
            My Teams
          </Link>
          
          {/* Support link in mobile menu */}
          <Link
            to="/support"
            style={{
              ...styles.mobileLink,
              ...(isActive("/support") ? styles.activeLink : {})
            }}
            onClick={() => setIsMenuOpen(false)}
          >
            Support
          </Link>
          
          {/* My Account link in mobile menu - now shown to everyone */}
          <Link
            to="/my-account"
            style={{
              ...styles.mobileLink,
              ...(isActive("/my-account") ? styles.activeLink : {})
            }}
            onClick={() => setIsMenuOpen(false)}
          >
            My Account
          </Link>
          
          {/* Conditional rendering for other options */}
          {isAuthenticated ? (
            <Link
              to="/team-options"
              style={{
                ...styles.mobileLink,
                ...(isActive("/team-options") ? styles.activeLink : {})
              }}
              onClick={() => setIsMenuOpen(false)}
            >
              Team Options
            </Link>
          ) : (
            <>
              <Link 
                to="/login" 
                style={styles.mobileCta}
                onClick={() => setIsMenuOpen(false)}
              >
                Log In
              </Link>
              <Link 
                to="/signup" 
                style={styles.mobileCta}
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export default Header;